"use client";

import { useLingui } from "@lingui/react/macro";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import { cn } from "@/lib/utils";
import { AnalysisTargetCommandGroups } from "./analysis-target-command-groups";
import {
  type AnalysisTargetOptionsState,
  filterAnalysisTargetOptionGroups,
} from "./analysis-target-options";

/**
 * 入力テキスト内のメンション候補範囲（`@query`）を表す。
 */
export type AnalysisTargetMentionMatch = {
  query: string;
  start: number;
  end: number;
  trigger: "@" | "＠";
};

export type AnalysisTargetMentionAnchor = {
  left: number;
  top: number;
};

/**
 * カーソル位置の直前が `@query` または `＠query` 形式かを判定する。
 *
 * 契約: 空白または行頭の trigger のみをメンション開始として扱う
 * 失敗時: メンションでなければ `null` を返す
 */
export function findAnalysisTargetMention(
  text: string,
  cursor: number,
): AnalysisTargetMentionMatch | null {
  const safeCursor = Math.max(0, Math.min(cursor, text.length));
  const beforeCursor = text.slice(0, safeCursor);
  const match = /(^|\s)([@＠])([^\s@＠]*)$/u.exec(beforeCursor);
  if (!match) {
    return null;
  }
  const matchedText = match[0];
  const trigger = (match[2] ?? "@") as AnalysisTargetMentionMatch["trigger"];
  const atOffset = matchedText.lastIndexOf(trigger);
  if (atOffset < 0) {
    return null;
  }
  return {
    query: match[3] ?? "",
    start: match.index + atOffset,
    end: safeCursor,
    trigger,
  };
}

const CARET_MEASURE_STYLE_PROPS = [
  "boxSizing",
  "width",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "lineHeight",
  "textTransform",
  "textIndent",
  "textAlign",
  "wordBreak",
  "overflowWrap",
  "tabSize",
] as const;

const MENTION_MENU_STICKY_HEADER_HEIGHT_PX = 32;

/**
 * テキストエリア内の指定カーソル位置を、InputGroup 内で使える座標へ変換する。
 */
export function getAnalysisTargetMentionAnchor(
  textarea: HTMLTextAreaElement,
  cursor: number,
): AnalysisTargetMentionAnchor | null {
  const safeCursor = Math.max(0, Math.min(cursor, textarea.value.length));
  const computedStyle = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");

  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.pointerEvents = "none";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordWrap = "break-word";

  CARET_MEASURE_STYLE_PROPS.forEach((property) => {
    mirror.style[property] = computedStyle[property];
  });

  mirror.textContent = textarea.value.slice(0, safeCursor);
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  try {
    const textareaRect = textarea.getBoundingClientRect();

    const maxLeft = Math.max(12, window.innerWidth - 360 - 12);
    const nextLeft = Math.min(
      maxLeft,
      Math.max(12, textareaRect.left + marker.offsetLeft - textarea.scrollLeft),
    );
    const nextTop = Math.max(
      0,
      textareaRect.top + marker.offsetTop - textarea.scrollTop,
    );
    return {
      left: nextLeft,
      top: nextTop,
    };
  } finally {
    document.body.removeChild(mirror);
  }
}

type AnalysisTargetMentionMenuProps = {
  open: boolean;
  query: string;
  optionsState: AnalysisTargetOptionsState;
  onSelectTarget: (target: AnalysisTarget) => void;
  anchor: AnalysisTargetMentionAnchor | null;
  activeOptionKey?: string | null;
  onActiveOptionKeyChange?: (key: string) => void;
  className?: string;
};

/**
 * `@` 入力中に表示する候補サジェストメニュー。
 *
 * 既存ピッカーと同じ候補ソース/見た目（Command UI）を使い、選択は呼び出し元へ委譲する。
 */
export function AnalysisTargetMentionMenu({
  open,
  query,
  optionsState,
  onSelectTarget,
  anchor,
  activeOptionKey,
  onActiveOptionKeyChange,
  className,
}: AnalysisTargetMentionMenuProps) {
  const { i18n, t } = useLingui();
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !anchor || !activeOptionKey) {
      return;
    }
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }
    // キーボード移動時に、ハイライト行が可視領域から外れないよう追従する。
    const activeElement = listElement.querySelector<HTMLElement>(
      `[data-analysis-target-key="${activeOptionKey}"]`,
    );
    if (!activeElement) {
      return;
    }

    /**
     * `scrollIntoView({ block: "nearest" })` だと sticky ヘッダー分の高さを考慮できず、
     * アクティブ行が見えていないのに「可視」と判定される場合がある。
     * そのため、リストと要素の矩形差分から scrollTop を手動補正して、
     * キーボード移動時の欠け/ガタつきを抑える。
     */
    const listRect = listElement.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const topBoundary = listRect.top + MENTION_MENU_STICKY_HEADER_HEIGHT_PX;
    const bottomBoundary = listRect.bottom;

    if (activeRect.top < topBoundary) {
      listElement.scrollTop -= topBoundary - activeRect.top;
      return;
    }
    if (activeRect.bottom > bottomBoundary) {
      listElement.scrollTop += activeRect.bottom - bottomBoundary;
    }
  }, [activeOptionKey, anchor, open]);

  if (!open || !anchor) {
    return null;
  }

  // project 名一致時でも配下候補を表示し、階層を保ったまま絞り込みできるようにする。
  const filteredGroups = filterAnalysisTargetOptionGroups(
    optionsState.groupedOptions,
    query,
    { locale: i18n.locale },
  );
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    // InputGroup の通常フローに載せると高さが崩れるため、常に viewport 固定オーバーレイで描画する。
    <div
      className={cn("pointer-events-none fixed z-50", className)}
      style={{
        left: `${anchor.left}px`,
        top: `${anchor.top}px`,
      }}
    >
      <div className="pointer-events-auto w-[360px] max-w-[min(360px,calc(100vw-2rem))] -translate-y-[calc(100%+8px)] overflow-hidden rounded-md border bg-popover shadow-md">
        <Command shouldFilter={false}>
          <CommandList ref={listRef} className="max-h-[260px] scroll-pt-8">
            <CommandEmpty>
              {optionsState.error
                ? t`Failed to load targets`
                : t`No matching project or view`}
            </CommandEmpty>
            {optionsState.fetching ? (
              <CommandGroup heading={t`Loading`}>
                <CommandItem value="loading" disabled>
                  {t`Loading targets...`}
                </CommandItem>
              </CommandGroup>
            ) : null}
            <AnalysisTargetCommandGroups
              groupedOptions={filteredGroups}
              selectedTargets={[]}
              onSelectTarget={onSelectTarget}
              activeOptionKey={activeOptionKey}
              onActiveOptionKeyChange={onActiveOptionKeyChange}
            />
          </CommandList>
        </Command>
      </div>
    </div>,
    document.body,
  );
}
